import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {
  getFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ
} from './_request';

const defaultForm = {
  question: '',
  answer: '',
  ordering: 0,
};

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const res = await getFAQs();
      setFaqs(res.data || []);
    } catch (err) {
      setError('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (faq) => {
    setForm({ ...faq });
    setEditingId(faq.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    setLoading(true);
    try {
      await deleteFAQ(id);
      fetchFaqs();
    } catch (err) {
      setError('Failed to delete FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (editingId) {
        await updateFAQ(editingId, form);
      } else {
        await createFAQ(form);
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchFaqs();
    } catch (err) {
      setError('Failed to save FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">FAQ Management</h2>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add New
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center"><CircularProgress /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Question</th>
                <th className="p-2 border">Answer</th>
                <th className="p-2 border">Order</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((faq) => (
                <tr key={faq.id}>
                  <td className="p-2 border">{faq.question}</td>
                  <td className="p-2 border">{faq.answer}</td>
                  <td className="p-2 border text-center">{faq.ordering}</td>
                  <td className="p-2 border text-center">
                    <IconButton onClick={() => handleEdit(faq)}><EditIcon /></IconButton>
                    <IconButton onClick={() => handleDelete(faq.id)}><DeleteIcon /></IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <form className="mt-8 max-w-xl mx-auto bg-white p-6 rounded shadow" onSubmit={handleSubmit}>
          <h3 className="text-xl font-semibold mb-4">{editingId ? 'Edit' : 'Add'} FAQ</h3>
          <div className="mb-4">
            <TextField
              label="Question"
              name="question"
              value={form.question}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </div>
          <div className="mb-4">
            <TextField
              label="Answer"
              name="answer"
              value={form.answer}
              onChange={handleInputChange}
              fullWidth
              multiline
              minRows={2}
              required
            />
          </div>
          <div className="mb-4">
            <TextField
              label="Order"
              name="ordering"
              type="number"
              value={form.ordering}
              onChange={handleInputChange}
              fullWidth
            />
          </div>
          {error && <div className="text-red-500 mb-2">{error}</div>}
          <div className="flex gap-4">
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button variant="outlined" onClick={() => { setShowForm(false); setEditingId(null); setForm(defaultForm); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FAQManagement;
